import React, { useState, useEffect } from 'react';
import supabase from '../../supabase/supabase';
import { FiSave, FiCreditCard, FiShield, FiRefreshCw, FiAlertTriangle, FiCheckCircle, FiActivity, FiTerminal, FiEye } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AdminPaymentTransactions from './AdminPaymentTransactions';

const AdminPayments = () => {
  const [activeTab, setActiveTab] = useState('config');
  const [gateway, setGateway] = useState({
    is_active: false,
    config: { environment: 'sandbox', merchant_id: '', currency: 'LKR' }
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchGateway();
  }, []);

  const fetchGateway = async () => {
    // using maybeSingle() here as well to be safe
    const { data } = await supabase.from('payment_gateways').select('*').eq('name', 'marx_ipg').maybeSingle();
    if (data) {
      setGateway({ is_active: data.is_active, config: data.config });
    }
  };

  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setGateway(prev => ({ ...prev, config: { ...prev.config, [name]: value } }));
  };

  const handleToggleActive = (e) => {
    setGateway(prev => ({ ...prev, is_active: e.target.checked }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await supabase.from('payment_gateways').update({
        is_active: gateway.is_active,
        config: gateway.config,
        updated_at: new Date().toISOString()
      }).eq('name', 'marx_ipg');
      alert('Gateway configuration saved successfully!');
    } catch (error) {
      alert('Failed to save settings.');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const { data, error } = await supabase.functions.invoke('marx-test-connection', {
        headers: {
          Authorization: `Bearer ${session?.access_token}`
        }
      });
      if (error) throw error;
      setTestResult(data);
    } catch (err) {
      setTestResult({ success: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      <div className="flex gap-4 border-b border-gray-200">
        <button 
          onClick={() => setActiveTab('config')}
          className={`pb-4 px-2 font-bold text-sm uppercase tracking-wider transition-colors ${activeTab === 'config' ? 'border-b-2 border-brand-gold text-brand-darkSage' : 'text-gray-400 hover:text-gray-700'}`}>
          Gateway Configuration
        </button>
        <button 
          onClick={() => setActiveTab('transactions')}
          className={`pb-4 px-2 font-bold text-sm uppercase tracking-wider transition-colors ${activeTab === 'transactions' ? 'border-b-2 border-brand-gold text-brand-darkSage' : 'text-gray-400 hover:text-gray-700'}`}>
          Transactions & Logs
        </button>
      </div>

      {activeTab === 'config' && (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8">
          <div className="flex items-center gap-3 mb-6 border-b border-gray-100 pb-4">
            <SafeIcon icon={FiShield} className="text-brand-gold w-6 h-6" />
            <h2 className="text-xl font-serif text-gray-800">Marx IPG Configuration</h2>
          </div>

          <form onSubmit={handleSave} className="space-y-6 max-w-3xl">
            <div className="bg-gray-50/50 p-6 rounded-xl border border-gray-100 space-y-6">
              <div className="bg-blue-50 text-blue-800 p-4 rounded-lg flex gap-3 text-sm border border-blue-100">
                <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 shrink-0 mt-0.5" />
                <div>
                  <strong>Security Notice:</strong> Sensitive data like <code>MARX_API_KEY</code> and <code>MARX_WEBHOOK_SECRET</code> are strictly managed via Supabase Edge Function Secrets to comply with Enterprise Security Standards.
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800">Enable Payment Gateway</h3>
                  <p className="text-xs text-gray-500">Allow customers to pay via Marx IPG v4</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={gateway.is_active} onChange={handleToggleActive} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-gold/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-darkSage"></div>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Environment</label>
                  <select name="environment" value={gateway.config.environment} onChange={handleConfigChange} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-brand-gold bg-white">
                    <option value="sandbox">Sandbox (Testing)</option>
                    <option value="production">Production (Live)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Currency</label>
                  <select name="currency" value={gateway.config.currency} onChange={handleConfigChange} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-brand-gold bg-white">
                    <option value="LKR">LKR (Sri Lankan Rupee)</option>
                    <option value="USD">USD (US Dollar)</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Merchant ID</label>
                  <input type="text" name="merchant_id" value={gateway.config.merchant_id} onChange={handleConfigChange} className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-brand-gold" placeholder="e.g. MRX-123456" />
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button type="submit" disabled={saving} className="flex-1 flex items-center justify-center gap-2 bg-brand-darkSage text-white py-3 rounded-xl font-bold tracking-widest uppercase text-xs hover:bg-brand-gold transition-colors disabled:opacity-50">
                <SafeIcon icon={FiSave} className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
              <button type="button" onClick={handleTestConnection} disabled={testing} className="flex-1 flex items-center justify-center gap-2 bg-gray-100 text-gray-700 py-3 rounded-xl font-bold tracking-widest uppercase text-xs hover:bg-gray-200 border border-gray-300 transition-colors disabled:opacity-50">
                <SafeIcon icon={FiActivity} className="w-4 h-4" />
                {testing ? 'Testing...' : 'Test Gateway Connection'}
              </button>
            </div>

            {testResult && (
              <div className={`p-4 rounded-xl text-sm font-mono border ${testResult.success ? 'bg-green-50 text-green-800 border-green-200' : 'bg-red-50 text-red-800 border-red-200'}`}>
                <div className="font-bold mb-2 flex items-center gap-2">
                  <SafeIcon icon={testResult.success ? FiCheckCircle : FiAlertTriangle} />
                  {testResult.success ? 'Connection Successful' : 'Connection Failed'}
                </div>
                <p>{testResult.message || testResult.error}</p>
                {testResult.details && (
                  <pre className="mt-2 text-[10px] overflow-x-auto bg-white/50 p-2 rounded border border-black/10">
                    {JSON.stringify(testResult.details, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </form>
        </div>
      )}

      {activeTab === 'transactions' && <AdminPaymentTransactions />}
    </div>
  );
};

export default AdminPayments;